import * as React from 'react'
import { styled } from '@mui/material/styles'
import Image from 'next/image'

import { Box, Stack, Grid, Divider, Button } from '@mui/material'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import Text from 'components/atomic/atoms/text'
import Card from 'components/atomic/atoms/card'
import Chip from 'components/atomic/atoms/chip'
import Icon from 'components/atomic/atoms/icon'

const AmazingCard = styled('div')`
    position: relative;
    display: flex;
    flex-direction: column;
    row-gap: 0.3rem;
    border: 1px solid transparent;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    background: ${({ theme }) => theme.palette.background.default};
    background-clip: padding-box;
    padding: 1rem;
    &:before {
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: -1px;
        right: -1px;
        background: linear-gradient(45deg, #ecd37c, #6a5a04);
        content: '';
        z-index: -1;
        border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    }
`

const PricingCardHorizontal = (features = []) => (
    <Box mt={2}>
        <Card>
            <Stack direction="row">
                <Stack spacing="1rem" flex={3}>
                    <Text variant="h3" color="primary">
                        Pro Supporter
                    </Text>
                    {/* <div>
                                    <Alert variant="outlined" severity="warning">
                                        Solo 12/200 rimasti
                                    </Alert>
                                </div> */}

                    <Stack direction="row" spacing="1rem" justifyContent="space-between" alignItems="center">
                        <Chip label={'Solo 12/220 rimasti'} size="small" />
                    </Stack>

                    <Stack>
                        <Text variant="h3">0 €</Text>
                        <Text variant="subtitle2">per sempre</Text>
                        <Text mt={2} width={{ sm: '70%' }}>
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aliquam eget eros purus.
                        </Text>
                    </Stack>
                    <Button variant="contained" color="primary" disabled fullWidth>
                        Non disponibile
                    </Button>
                </Stack>

                <Divider orientation="vertical" />

                <Stack spacing={'0.5rem'} mt={1} flex={3}>
                    <Text variant="subtitle1">Incluso</Text>
                    {features.map((item) => (
                        <div key={`included${item}`} style={{ display: 'flex', alignItems: 'center' }}>
                            <Icon name="taskAliIcon" sx={{ color: '#ecd37c', marginRight: '0.5rem' }} />
                            <Text variant="body2">{item}</Text>
                        </div>
                    ))}
                </Stack>
                <Box flex={2} position="relative">
                    <Image
                        alt="Pricing 1"
                        // width="auto"
                        // height="100%"
                        // objectFit="fill"
                        layout="fill"
                        // style={{ opacity: 0.4 }}
                        src={'/assets/paths/path-icon-1.webp'}
                    />
                </Box>
            </Stack>
        </Card>
    </Box>
)

interface PricingCardProps {
    children: React.ReactNode
    amazing?: boolean
}

export const PricingCard = ({ children, amazing = false }: PricingCardProps) => {
    const CardType = amazing ? AmazingCard : Card
    return (
        <Grid item xs={12} sm={6} md={4}>
            <CardType>
                <Stack spacing={2}>{children}</Stack>
            </CardType>
        </Grid>
    )
}

interface PricingCardHeadingProps {
    available?: boolean
    amazing?: boolean
    label?: boolean
    plan: 'starter' | 'pro' | 'supporter'
    onClick?: (plan: string) => void
}

export const PricingCardHeading = (props: PricingCardHeadingProps) => {
    const { available = true, amazing = false, label = true, plan, onClick } = props
    const { t }: I18n = useTranslation()

    return (
        <>
            <Stack direction="row" spacing="1rem" justifyContent="space-between" alignItems="center">
                <Text variant="h3" color={available ? (amazing ? '#ecd37c' : 'primary') : 'text.secondary'}>
                    {t(`pricing:plans.${plan}.title`)}
                </Text>
                {label && <Chip label={t(`pricing:plans.${plan}.label`)} size="small" />}
            </Stack>

            <Stack textAlign="center" alignItems="center" color={!available ? 'text.secondary' : ''}>
                <Text variant="h3"> {t(`pricing:plans.${plan}.price`)}</Text>
                <Text variant="subtitle2"> {t(`pricing:plans.${plan}.renewalPeriod`)}</Text>
                <Text variant="body2" mt={2} width={{ sm: '90%' }}>
                    {t(`pricing:plans.${plan}.description`)}
                </Text>
            </Stack>

            {available && amazing ? (
                <Button
                    variant="contained"
                    sx={{ backgroundColor: '#ecd37c' }}
                    fullWidth
                    onClick={() => (onClick ? onClick(plan) : null)}>
                    {t(`pricing:plans.${plan}.buttonText`)}
                </Button>
            ) : (
                <Button
                    color="primary"
                    disabled={!available}
                    fullWidth
                    onClick={() => (onClick ? onClick(plan) : null)}>
                    {t(`pricing:plans.${plan}.buttonText`)}
                </Button>
            )}
        </>
    )
}

interface PricingCardFeaturesProps {
    plan: 'starter' | 'pro' | 'supporter'
    available?: boolean
}

export const PricingCardFeatures = ({ plan, available = true }: PricingCardFeaturesProps) => {
    const { t }: I18n = useTranslation()

    /** TODO: translate */
    const featuresMap = {
        starter: ['Accesso base a tutti i corsi gratuiti', 'Supporto della community', 'Risorse di apprendimento'],
        pro: [
            'Accesso illimitato a tutti i corsi',
            'Supporto della community',
            'Risorse di apprendimento',
            'Quiz interattivi',
            'Il tuo percorso personalizzato',
            'Percorsi per costruire una skill',
            'Percorsi per costruire una carriera',
            'Costruisci progetti reali',
            'Certificato di completamento',
            'Il tuo team di sviluppo',
            'Supporto prioritario',
            'Accesso illimitato a Devv Club',
        ],
        supporter: [
            'Tutte le funzionalità di Devv Pro per sempre',
            'Il tuo nome scritto per sempre nella storia di Devv',
            'Accesso VIP nel Devv Club',
            'Accesso anticipato a tutti i percorsi',
            'Ringraziamento personale dal fondatore (Matteo Giardino)',
        ],
    }

    return (
        <Stack spacing={1}>
            <Text variant="subtitle1">{t('pricing:planFeaturesIncluded')}</Text>
            {featuresMap[plan].map((item) => (
                <div key={`included${item}`} style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon
                        name="taskAliIcon"
                        sx={{ color: available ? '#ecd37c' : 'text.secondary', marginRight: '0.5rem' }}
                    />
                    <Text variant="body2">{item}</Text>
                </div>
            ))}
        </Stack>
    )
}
